import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  untracked,
  ViewChild,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject, debounceTime, filter, map, startWith, tap } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  AlwaysOpenStrategyDirective,
  CheckboxComponent,
  DropdownItemComponent,
  GroupsFeatureDirective,
  HeaderDefaultComponent,
  IconButtonComponent,
  IconComponent,
  MultiselectFeatureDirective,
  OverlayPosition,
  SearchFeatureDirective,
  Select2Component,
  Select2ListItemDirective,
  Select2TriggerDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { ViewFilterDef } from '@core/views-types';

import type { FilterSelectOptions } from '../../../services/filters-data.service';
import { FiltersDataService } from '../../../services/filters-data.service';

@Component({
  selector: 'rt-views-filter-multiselect',
  imports: [
    ReactiveFormsModule,
    Select2Component,
    GroupsFeatureDirective,
    SearchFeatureDirective,
    HeaderDefaultComponent,
    TranslatePipe,
    IconButtonComponent,
    IconComponent,
    Select2TriggerDirective,
    MultiselectFeatureDirective,
    AlwaysOpenStrategyDirective,
    CheckboxComponent,
    DropdownItemComponent,
    Select2ListItemDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: 'filter-multiselect.component.html',
})
export class FilterMultiselectComponent {
  public readonly overlayPosition = [
    OverlayPosition.BottomLeft,
    OverlayPosition.TopLeft,
    OverlayPosition.BottomRight,
    OverlayPosition.TopRight,
  ];

  public readonly selectedFilters = input.required<ViewFilterDef[]>();
  public readonly placeholder = input('app.views.filters.add_filter');
  public readonly backAlwaysVisible = input(false);

  public filterChange = output<ViewFilterDef[]>();
  public filterRemove = output<ViewFilterDef>();
  public filterAdd = output<ViewFilterDef>();
  public backClick = output<void>();

  public filterSelectControl = new FormControl<string[]>([]);
  public selectedGroup$ = new BehaviorSubject<string | null>(null);
  public filterSelectOptions: FilterSelectOptions | null = null;
  public hiddenItems: Select2Item[] = [];
  public searchControl = new FormControl<string | null>(null);

  @ViewChild('filterSelect', { static: true })
  private filterSelectCmp: Select2Component<Select2Item>;

  private filtersDataService = inject(FiltersDataService);
  private cdRef = inject(ChangeDetectorRef);
  private untilDestroyed = injectUntilDestroyed();

  private dataSourceFilterDefMap = toSignal(this.filtersDataService.dataSourceFilterDefMap$, {
    requireSync: true,
  });
  private scrollEndIndex$ = new BehaviorSubject<number>(0);

  constructor() {
    effect(() => {
      const selectedFiltersIds = this.selectedFilters().map(def => def.id);
      untracked(() => this.filterSelectControl.setValue(selectedFiltersIds));
      this.hiddenItems = this.selectedFilters().map(def =>
        this.filtersDataService.filterToSelectOption(def),
      );
    });
  }

  public ngOnInit() {
    const searchQuery$ = this.searchControl.valueChanges.pipe(
      debounceTime(100),
      startWith(this.searchControl.value),
      map(value => (value ?? '').trim().toLowerCase()),
      tap(() => {
        this.filterSelectCmp.scrollTop();
        this.scrollEndIndex$.next(0);
      }),
    );

    this.filtersDataService
      .getFilterSelectOptions$(
        this.selectedGroup$,
        searchQuery$,
        this.scrollEndIndex$.pipe(filter(index => index > 0)),
      )
      .pipe(this.untilDestroyed)
      .subscribe(selectOptions => {
        this.filterSelectOptions = selectOptions;
        this.cdRef.detectChanges();
      });
  }

  public onItemClick(item: Select2Item) {
    if (this.isFilterGroup(item.value)) {
      this.selectedGroup$.next(item.value);
    } else {
      this.filterSelectCmp.selectController.select$.next(item);
      this.updateSelection(item);
    }
  }

  public isFilterGroup(filterId: string) {
    return this.dataSourceFilterDefMap()[filterId]?.type === 'filter_group';
  }

  public vsCompareItems = (item1: Select2Item | undefined, item2: Select2Item | undefined) =>
    item1?.label === item2?.label;

  public onBackClick() {
    this.selectedGroup$.next(null);
    if (!this.filterSelectOptions?.groupDef) {
      this.backClick.emit();
    }
  }

  public onScrollEnd(event: IPageInfo) {
    const endIndex = event.endIndexWithBuffer;
    if (endIndex !== -1 && endIndex < this.scrollEndIndex$.value) return;

    this.scrollEndIndex$.next(endIndex);
  }

  private updateSelection(item: Select2Item) {
    const controlValue = this.filterSelectControl.value!;
    const filterDef = this.filtersDataService.filterDefMap.get(item.value)!;
    const isSelected = controlValue.some(id => id === item.value);
    this.hiddenItems = isSelected
      ? [...this.hiddenItems, item]
      : this.hiddenItems.filter(def => def.value !== item.value);
    const newSelectedFilters = isSelected
      ? [...this.selectedFilters(), filterDef]
      : this.selectedFilters().filter(def => def.id !== item.value);
    isSelected ? this.filterAdd.emit(filterDef) : this.filterRemove.emit(filterDef);
    this.filterChange.emit(newSelectedFilters);
  }
}
